/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/media-has-caption */
import { Typography } from '@mui/material';
import useFileUpload from 'base/core/hooks/use-file-upload';
import { ClosableModal, PrimaryLoadingButton, SecondaryButton } from 'components';
import { UploadScenarios } from 'interfaces/file-uploader';
import React from 'react';
import { urltoFile } from 'utils/file-uploader';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
  videoExt: string;
  onFileCreate: ({ fileName, url }: { fileName: string, url: string }) => void;
}

const GenerateVideoThumbnail = ({
  open, onClose, videoUrl, videoExt = 'mp4', onFileCreate,
}: Props) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const i = React.useRef<number | undefined>(undefined);
  const [stateValue, setStateValue] = React.useState(0);
  const { progress, handleContentDataUpload } = useFileUpload();

  const [loading, setLoading] = React.useState(false);

  const draw = React.useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      const times = video.videoWidth / 428;
      const vHeight = video.videoHeight / times;
      canvas.width = 428;
      canvas.height = vHeight;
      i.current = window.requestAnimationFrame(draw);

      const context = canvas?.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, 428, vHeight);
      }
    }
  }, []);

  const cancelAnimation = () => {
    window.cancelAnimationFrame(i.current ?? 0);
    i.current = undefined;
  };

  const videoMimeType = React.useMemo(() => {
    switch (videoExt) {
      case 'webm':
        return 'video/webm';
      case 'm3u8':
        return 'application/x-mpegURL';
      default:
        return 'video/mp4';
    }
  }, [videoExt]);

  React.useEffect(() => {
    const video = videoRef.current;
    video?.addEventListener('loadeddata', draw, false);
    video?.addEventListener('play', draw, false);
    video?.addEventListener('pause', cancelAnimation, false);
    video?.addEventListener('ended', cancelAnimation, false);
  }, [stateValue, draw]);

  React.useEffect(() => {
    setStateValue(stateValue + 1);
  }, [open]);

  React.useEffect(() => {
    const video = videoRef.current;
    return () => {
      video?.removeEventListener('loadeddata', draw, false);
      video?.removeEventListener('play', draw, false);
      video?.removeEventListener('pause', cancelAnimation, false);
      video?.removeEventListener('ended', cancelAnimation, false);
    };
  }, []);

  const generateThumbnail = async () => {
    const canvas = canvasRef.current;
    if (canvas) {
      setLoading(true);
      const imageUrl = canvas.toDataURL('image/jpeg', 1.0);
      const file = await urltoFile(imageUrl, `thumb-${uuidv4()}.jpeg`, 'image/jpeg');
      const res = await handleContentDataUpload(
        null,
        '',
        UploadScenarios.generateThumbnail,
        file,
      );
      setTimeout(() => {
        onFileCreate({
          fileName: `media/${res.name}${res.extension}`,
          url: res.url,
        });
        setLoading(false);
      }, 3000);
    }
  };

  return (
    <ClosableModal
      open={open}
      onClose={onClose}
      title="Generate the video thumbnail"
      subTitle=""
      modalClass=""
    >
      <Typography variant="body2" className="mb-2">Drag the video slider to select the frame</Typography>
      <video
        ref={videoRef}
        controls
        width="100%"
        crossOrigin="anonymous"
        className="mb-2"
      >
        <source src={videoUrl} type={videoMimeType} />
        Your browser does not support the video tag.
      </video>
      <Typography variant="body2" className="mb-2">Thumbnail preview:</Typography>
      <canvas ref={canvasRef} style={{ borderWidth: 5, borderColor: 'rgb(68 68 68)', borderStyle: 'solid' }} />
      <div className="row mt-2">
        <div className="col-12 col-md-6 mb-2 mb-md-0">
          <SecondaryButton fullWidth onClick={onClose}>
            Cancel
          </SecondaryButton>
        </div>
        <div className="col-12 col-md-6">
          <PrimaryLoadingButton
            fullWidth
            loading={loading}
            disabled={loading || !videoRef.current || !canvasRef.current || progress > 0}
            onClick={generateThumbnail}
          >
            Generate & Save
          </PrimaryLoadingButton>
        </div>
      </div>
    </ClosableModal>
  );
};

export default GenerateVideoThumbnail;
